import * as React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useCanAccess } from '@react-admin/ra-rbac';
import {
    ListPaginationContext,
    Pagination,
    useDataProvider,
    useGetIdentity,
    useNotify,
    useTranslate,
} from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { SchoolMergeContext } from './mergeNavigation';
import {
    formatMergeCancelDeadline,
    formatMergeMpassStatus,
    MergeMpassStatus,
} from './mergeReport';

interface HistoryAccount {
    guid: string;
    name: string;
    identifier?: string;
}

interface HistoryEntry {
    guid: string;
    to: HistoryAccount;
    from: HistoryAccount;
    created: string;
    status: number;
    mpass_status?: MergeMpassStatus;
    materials_merged: boolean;
    cancellable: boolean;
    cancel_deadline: string;
}

const MergeHistoryPage = ({
    resource,
    school,
    onBusyChange,
}: SchoolMergeContext) => {
    const valid =
        ['student', 'pupil', 'teacher'].includes(resource) &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            school
        );
    const { canAccess, isPending } = useCanAccess({ action: 'read', resource });
    const { canAccess: canCancel } = useCanAccess({
        action: 'cancel_merge',
        resource,
    });
    const dataProvider = useDataProvider();
    const { identity, isPending: identityPending } = useGetIdentity();
    const notify = useNotify();
    const translate = useTranslate();
    const [page, setPage] = React.useState(1);
    const [perPage, setPerPage] = React.useState(25);
    const [searchInput, setSearchInput] = React.useState('');
    const [search, setSearch] = React.useState('');
    const [entries, setEntries] = React.useState<HistoryEntry[]>([]);
    const [hasMore, setHasMore] = React.useState(false);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [selected, setSelected] = React.useState<string[]>([]);
    const [pending, setPending] = React.useState<HistoryEntry[] | null>(null);
    const [cancelling, setCancelling] = React.useState(false);
    const [completed, setCompleted] = React.useState(0);
    const [cancelResult, setCancelResult] = React.useState<{
        completed: number;
        total: number;
        failed?: string;
    } | null>(null);
    const [revision, refresh] = React.useReducer(value => value + 1, 0);

    const isCancellable = (entry: HistoryEntry) =>
        !identityPending &&
        entry.status === 0 &&
        !entry.materials_merged &&
        entry.cancellable &&
        // RoleSwitcher only changes the client identity, not the backend token.
        (identity?._originalUserrights === undefined ||
            (identity.userrights ?? 0) >= 400 ||
            Date.now() < new Date(entry.cancel_deadline).getTime());

    React.useEffect(() => {
        onBusyChange?.(cancelling);
        return () => onBusyChange?.(false);
    }, [cancelling, onBusyChange]);

    React.useEffect(() => {
        setPage(1);
    }, [resource, school]);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    React.useEffect(() => {
        if (!valid || !canAccess) return;
        let active = true;
        setLoading(true);
        setError('');
        setEntries([]);
        setSelected([]);
        const query = new URLSearchParams({
            resource,
            school,
            page: String(page),
            per_page: String(perPage),
            q: search,
        });
        dataProvider
            .getAny('user/merge-history', query.toString())
            .then(
                ({
                    data,
                }: {
                    data: {
                        data: HistoryEntry[];
                        has_more: boolean;
                        total: number;
                    };
                }) => {
                    if (!active) return;
                    setEntries(data.data);
                    setHasMore(data.has_more);
                    setTotal(data.total);
                }
            )
            .catch((err: Error) => {
                console.error(err);
                if (active) setError(translate('merge.history.load_error'));
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [
        valid,
        canAccess,
        resource,
        school,
        page,
        perPage,
        search,
        revision,
        dataProvider,
        translate,
    ]);

    const cancel = async () => {
        if (!pending?.length || cancelling) return;
        if (!canCancel || pending.some(entry => !isCancellable(entry))) {
            notify('merge.profilepage.cancel_error', { type: 'error' });
            setPending(null);
            return;
        }
        setCancelling(true);
        setCompleted(0);
        setCancelResult(null);
        let count = 0;
        let failed: string | undefined;
        const ordered = [...pending].sort(
            (a, b) =>
                new Date(b.created).getTime() - new Date(a.created).getTime() ||
                b.guid.localeCompare(a.guid)
        );
        try {
            for (const entry of ordered) {
                try {
                    if (!isCancellable(entry)) {
                        throw new Error(
                            translate('merge.profilepage.cancel_error')
                        );
                    }
                    await dataProvider.cancelUserMerge(entry.guid);
                    count += 1;
                    setCompleted(count);
                } catch (err) {
                    console.error(err);
                    failed = `${entry.from.identifier || entry.from.name}: ${
                        err instanceof Error
                            ? err.message
                            : translate('merge.profilepage.cancel_error')
                    }`;
                    break;
                }
            }
            setCancelResult({
                completed: count,
                total: pending.length,
                failed,
            });
            setPending(null);
            notify(
                failed
                    ? 'merge.profilepage.cancel_error'
                    : 'merge.profilepage.cancel_success',
                { type: failed ? 'error' : 'success' }
            );
            refresh();
        } finally {
            setCancelling(false);
        }
    };

    const selectable = canCancel ? entries.filter(isCancellable) : [];
    const selectedEntries = selectable.filter(entry =>
        selected.includes(entry.guid)
    );

    const formatAccount = (account: HistoryAccount) => (
        <>
            <Typography variant="body2">{account.name}</Typography>
            <Typography variant="caption">
                {account.identifier || '—'}
            </Typography>
        </>
    );

    const accountLink = (account: HistoryAccount) => (
        <Link
            component={RouterLink}
            to={`/${resource}/${encodeURIComponent(account.guid)}`}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
            <Box>{formatAccount(account)}</Box>
            <EditIcon fontSize="small" />
        </Link>
    );

    if (!valid)
        return (
            <Alert severity="error">
                {translate('merge.bulk.invalid_context')}
            </Alert>
        );
    if (isPending) return <CircularProgress />;
    if (!canAccess)
        return (
            <Alert severity="warning">
                {translate('merge.bulk.no_permission')}
            </Alert>
        );

    return (
        <Box p={2}>
            <Stack direction="row" justifyContent="space-between" mb={2}>
                <Typography variant="h5">
                    {translate('merge.history.open')}
                </Typography>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
                size="small"
                type="search"
                inputProps={{ maxLength: 200 }}
                label={translate('merge.history.search')}
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
                disabled={cancelling}
                sx={{ my: 1, mr: 1, minWidth: 280 }}
            />
            <Button onClick={refresh} disabled={loading || cancelling}>
                {translate('ra.action.refresh')}
            </Button>
            {canCancel && (
                <Button
                    color="error"
                    disabled={!selectedEntries.length || loading || cancelling}
                    onClick={() => setPending(selectedEntries)}
                >
                    {translate('merge.history.cancel_selected', {
                        count: selectedEntries.length,
                    })}
                </Button>
            )}
            {cancelResult && (
                <Alert
                    severity={cancelResult.failed ? 'error' : 'success'}
                    sx={{ my: 1 }}
                >
                    {translate('merge.history.cancel_result', {
                        completed: cancelResult.completed,
                        total: cancelResult.total,
                    })}
                    {cancelResult.failed && (
                        <Typography variant="body2">
                            {translate('merge.history.cancel_stopped')}{' '}
                            {cancelResult.failed}
                        </Typography>
                    )}
                </Alert>
            )}
            {loading ? (
                <CircularProgress />
            ) : (
                <Paper sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {canCancel && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            inputProps={{
                                                'aria-label': translate(
                                                    'merge.history.select_page'
                                                ),
                                            }}
                                            disabled={
                                                !selectable.length || cancelling
                                            }
                                            checked={
                                                selectable.length > 0 &&
                                                selectedEntries.length ===
                                                    selectable.length
                                            }
                                            indeterminate={
                                                selectedEntries.length > 0 &&
                                                selectedEntries.length <
                                                    selectable.length
                                            }
                                            onChange={(_, checked) =>
                                                setSelected(
                                                    checked
                                                        ? selectable.map(
                                                              entry =>
                                                                  entry.guid
                                                          )
                                                        : []
                                                )
                                            }
                                        />
                                    </TableCell>
                                )}
                                {[
                                    'created',
                                    'primary',
                                    'source',
                                    'mpass_status',
                                    'status',
                                ].map(key => (
                                    <TableCell key={key}>
                                        {translate(`merge.history.${key}`)}
                                    </TableCell>
                                ))}
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entries.map(entry => (
                                <TableRow key={entry.guid}>
                                    {canCancel && (
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                inputProps={{
                                                    'aria-label': translate(
                                                        'merge.history.select_entry',
                                                        {
                                                            identifier:
                                                                entry.from
                                                                    .identifier ||
                                                                entry.from.name,
                                                        }
                                                    ),
                                                }}
                                                disabled={
                                                    !isCancellable(entry) ||
                                                    cancelling
                                                }
                                                checked={selectedEntries.some(
                                                    item =>
                                                        item.guid === entry.guid
                                                )}
                                                onChange={(_, checked) =>
                                                    setSelected(previous =>
                                                        checked
                                                            ? [
                                                                  ...previous,
                                                                  entry.guid,
                                                              ]
                                                            : previous.filter(
                                                                  guid =>
                                                                      guid !==
                                                                      entry.guid
                                                              )
                                                    )
                                                }
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        {new Date(
                                            entry.created
                                        ).toLocaleString()}
                                    </TableCell>
                                    <TableCell>
                                        {accountLink(entry.to)}
                                    </TableCell>
                                    <TableCell>
                                        {entry.status !== 0
                                            ? accountLink(entry.from)
                                            : formatAccount(entry.from)}
                                    </TableCell>
                                    <TableCell>
                                        {formatMergeMpassStatus(
                                            entry.mpass_status,
                                            translate
                                        )}
                                        {entry.status !== 0 && (
                                            <Typography
                                                variant="caption"
                                                display="block"
                                            >
                                                {translate(
                                                    'merge.history.mpass_cancelled'
                                                )}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {translate(
                                            entry.status !== 0
                                                ? 'merge.bulk.result_cancelled'
                                                : entry.materials_merged
                                                  ? 'merge.profilepage.cancel_materials_merged'
                                                  : !isCancellable(entry)
                                                    ? 'merge.profilepage.cancel_deadline_passed'
                                                    : 'merge.history.cancellable'
                                        )}
                                        {entry.status === 0 &&
                                            !entry.materials_merged &&
                                            isCancellable(entry) &&
                                            (identity?.userrights ?? 0) <
                                                400 && (
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                >
                                                    {translate(
                                                        'merge.history.cancellable_until',
                                                        {
                                                            date: formatMergeCancelDeadline(
                                                                entry.cancel_deadline
                                                            ),
                                                        }
                                                    )}
                                                </Typography>
                                            )}
                                    </TableCell>
                                    <TableCell>
                                        {canCancel && entry.status === 0 && (
                                            <Button
                                                color="error"
                                                disabled={
                                                    !isCancellable(entry) ||
                                                    cancelling
                                                }
                                                onClick={() =>
                                                    setPending([entry])
                                                }
                                            >
                                                {translate(
                                                    'merge.profilepage.cancel_button'
                                                )}
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {!entries.length && !error && (
                        <Typography p={2}>
                            {translate('merge.history.empty')}
                        </Typography>
                    )}
                </Paper>
            )}
            <ListPaginationContext.Provider
                value={{
                    resource,
                    page,
                    perPage,
                    total,
                    hasNextPage: hasMore,
                    hasPreviousPage: page > 1,
                    isLoading: loading,
                    isPending: loading || Boolean(error),
                    setPage: value => {
                        if (!loading && !cancelling) setPage(value);
                    },
                    setPerPage: value => {
                        if (loading || cancelling) return;
                        setPerPage(value);
                        setPage(1);
                    },
                }}
            >
                <Pagination rowsPerPageOptions={[10, 25, 50, 100, 200]} />
            </ListPaginationContext.Provider>
            <Dialog
                open={Boolean(pending)}
                onClose={() => {
                    if (!cancelling) setPending(null);
                }}
            >
                <DialogTitle>
                    {translate('merge.profilepage.cancel_confirm_title')}
                </DialogTitle>
                <DialogContent>
                    {pending?.length === 1 && (
                        <Typography>
                            {pending[0].from.identifier} →{' '}
                            {pending[0].to.identifier}
                        </Typography>
                    )}
                    {pending && pending.length > 1 && (
                        <Typography sx={{ mb: 1 }}>
                            {translate('merge.history.cancel_confirm', {
                                count: pending.length,
                            })}
                        </Typography>
                    )}
                    {translate('merge.profilepage.cancel_confirm_message')}
                    {cancelling && pending && (
                        <Typography role="status" sx={{ mt: 1 }}>
                            {translate('merge.history.cancel_progress', {
                                completed,
                                total: pending.length,
                            })}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={cancelling}
                        onClick={() => setPending(null)}
                    >
                        {translate('merge.cancel')}
                    </Button>
                    <Button
                        color="error"
                        disabled={
                            cancelling ||
                            !canCancel ||
                            !pending ||
                            !pending.length ||
                            pending.some(entry => !isCancellable(entry))
                        }
                        onClick={cancel}
                    >
                        {cancelling && (
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                        )}
                        {translate('merge.profilepage.cancel_button')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MergeHistoryPage;
